import { createContext } from 'react';

import { type Questionnaire } from '@/typings/Questionnaire';

type EventData = {
  test1: {
    id: string;
    data: string;
  };
  test2: {
    id: string;
    message: string;
  };
  docRequestUpdate: {
    id: string;
  };
  refreshUserWorkflowList: object;
  questionnaireResponseRestored: object;
  reRunAutofill: {
    field: Questionnaire.Field | Questionnaire.FieldCommon;
    contextId: string;
    prefetchedData?: Record<string, unknown>;
    prefetchedParams?: Record<string, unknown>;
  };
  populateCreditReportData: {
    contextId: string;
  };
  populatePayOrganizerData: {
    contextId: string;
  };
  populateClientQuestionnaireData: {
    contextId: string;
  };
  populateCaseData: {
    contextId: string;
  };
  autofillRerunning: {
    status: boolean;
    fieldId: string;
    contextId: string;
  };
  isResponseUpdating: {
    status: boolean;
  };
  fieldEditingStateChanged: {
    fieldKey: string;
    fieldType: string;
    isEditing: boolean;
  };
  questionnaireStateChange: {
    questionnaireId: string;
    state: 'saving' | 'waiting' | 'editing' | 'processing';
  };
  questionnaireListItemsRemoved: {
    deletedIds: string[];
  };
  reloadUserWorkflowDetail: object;
  importFromDataSource: {
    expressionVariableReferenceId: string;
    expressionVariableReferenceType: string;
    fieldKey: string;
  };
};

type Events = keyof EventData;

type Listener<EV extends Events> = (data: EventData[EV]) => void | Promise<void>;

export type EventsContextType = {
  addListener: <EV extends Events>(event: EV, fn: Listener<EV>) => Listener<EV>;
  emit: <EV extends Events>(event: EV, data: EventData[EV]) => void;
  isInitialized: boolean;
  removeListener: <EV extends Events>(event: EV, listener: Listener<EV>) => void;
};

const EventsContext = createContext<EventsContextType>({
  addListener: (event, listener) => listener,
  emit: () => {},
  isInitialized: false,
  removeListener: () => {},
});

export default EventsContext;
